import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { SwPush } from "@angular/service-worker";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { environment } from "../../environments";
import { Notification } from "../interfaces/notification";
import { NotificationConfig } from "../interfaces/v2/notification_config";
import { ScheduleService } from "./schedule.service";
import { Schedule } from "../interfaces/schedule";
import { tap, catchError, delay } from "rxjs/operators";
import { withErrorHandler } from './error-handler.util';
import { NotificationType } from "../../proto/lobbyze/realtime/data/notification";

export interface RegisterStatus {
  enabled: boolean
  err?: any
}

@Injectable({ providedIn: "root" })
export class NotificationService {
  private readonly baseURL = environment.API_URL + "notifications";
  private readonly baseURLv2 = environment.API_URL + "v2/notification";

  private registerStatus = new BehaviorSubject<RegisterStatus>({ enabled: false });

  constructor(
    private http: HttpClient,
  ) {}

  get() {
    return this.http.get<Notification[]>(this.baseURL + "/").pipe(
      withErrorHandler({ operation: 'getNotifications', cache: true, fallback: [] })
    );
  }

  view(ids: number[]) {
    return this.http.post(this.baseURL + "/view", { ids }).pipe(
      withErrorHandler({ operation: 'viewNotifications', timeoutMs: 30000, retries: 1 })
    );
  }

  dismiss(ids: number[]) {
    return this.http.post(this.baseURL + "/dismiss", { ids }).pipe(
      withErrorHandler({ operation: 'dismissNotifications', timeoutMs: 30000, retries: 1 })
    );
  }

  // before here, v1 api

  getScheduleNotification(schedules: number[]): Observable<any[]> {
    const cacheKey = Array.isArray(schedules)
      ? JSON.stringify([...schedules].sort((a, b) => a - b))
      : undefined;

    return this.http.post<any>(this.baseURLv2 + "/schedule", {
        schedules
      }
    ).pipe(
      withErrorHandler({
        operation: 'getScheduleNotification',
        cache: true,
        cacheKey,
      })
    );
  }

  updateScheduleNotification(notifications: Array<object>, ignore_warnings: boolean = true) {
    return this.http.put(this.baseURLv2 + "/schedule", {
      notifications,
      ignore_warnings
    }).pipe(
      withErrorHandler({ operation: 'updateScheduleNotification', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateScheduleNotification failed:', err);
        return throwError(() => err);
      }),
      tap(() => {
        localStorage.setItem("refresh-schedules", "true");
      })
    );
  }

  getNotificationTime(notifications) {
    return this.http.post(this.baseURLv2 + "/get-time", {
      notifications
    }).pipe(
      withErrorHandler({
        operation: 'getNotificationTime',
        cache: true,
        cacheKey: notifications == null ? undefined : JSON.stringify(notifications),
      })
    );
  }

  subscribe(subscription: PushSubscription) {
    return this.http.post(this.baseURLv2 + "/subscribe", {
      token: JSON.stringify(subscription),
    }, {
      observe: 'response'
    }).pipe(
      withErrorHandler({ operation: 'subscribe', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('subscribe failed:', err);
        return throwError(() => err);
      })
    );
  }

  getRegistrationStatus() {
    return this.registerStatus
  }

  getUpcoming() {
    return this.http.get<Schedule[]>(this.baseURLv2 + "/upcoming").pipe(
      withErrorHandler({ operation: 'getUpcoming', cache: true })
    );
  }


  pingNotification() {
    return this.http.post<any>(this.baseURLv2 + "/ping", null).pipe(
      withErrorHandler({ operation: 'pingNotification', timeoutMs: 30000, retries: 2 }),
      catchError(err => {
        console.error('pingNotification failed:', err);
        return of({});
      })
    );
  }

  resetNotifications(all_tiers: boolean): Observable<any> {
    return this.http.post(this.baseURLv2 + "/reset", { all_tiers }).pipe(
      withErrorHandler({ operation: 'resetNotifications', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('resetNotifications failed:', err);
        return throwError(() => err);
      })
    );
  }

  openLobby(tournament: number) {
    return this.http.post(this.baseURLv2 + "/open", {
      tournament
    }).pipe(
      withErrorHandler({ operation: 'openLobby', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('openLobby failed:', err);
        return throwError(() => err);
      })
    ).toPromise();
  }
}
